/**
 * Spotify Web API를 사용한 음악 분석
 *
 * 사용법:
 * 1. Spotify Developer Dashboard에서 Client ID/Secret 발급
 * 2. .env.local에 추가:
 *    SPOTIFY_CLIENT_ID=your_client_id
 *    SPOTIFY_CLIENT_SECRET=your_client_secret
 */

export interface SpotifyAudioFeatures {
  tempo: number;
  energy: number;
  valence: number;
  danceability: number;
  acousticness: number;
  instrumentalness: number;
  speechiness: number;
  key: number; // 0~11
  mode: number; // 0=minor, 1=major
  time_signature: number;
  duration_ms: number;
}

export interface SpotifyTrackMetadata {
  title: string;
  artist: string;
  album: string;
  albumArt: string;
  genres: string[];
  releaseDate: string;
  popularity: number;
  previewUrl?: string | null;
  explicit?: boolean;
  durationMs?: number;
  spotifyArtistId?: string;
  artistPopularity?: number;
  artistFollowers?: number;
}

interface SpotifySearchTrackItem {
  id: string;
  name: string;
  popularity: number;
  artists: Array<{ name: string }>;
}

interface SpotifySearchResponse {
  tracks?: {
    items: SpotifySearchTrackItem[];
  };
}

interface SpotifyArtistApiResponse {
  genres?: string[];
  popularity?: number;
  followers?: {
    total?: number;
  };
}

interface SpotifyTrackApiResponse {
  name: string;
  popularity: number;
  preview_url?: string | null;
  explicit?: boolean;
  duration_ms?: number;
  album: {
    name: string;
    release_date: string;
    images?: Array<{ url?: string }>;
  };
  artists: Array<{ id?: string; name: string }>;
}

interface SpotifyAudioAnalysis {
  track: {
    tempo: number;
    tempo_confidence: number;
    key: number;
    key_confidence: number;
    mode: number;
    mode_confidence: number;
    time_signature: number;
    duration: number;
  };
  sections: Array<{
    start: number;
    duration: number;
    confidence: number;
    loudness: number;
    tempo: number;
    key: number;
  }>;
}

// Spotify Access Token 가져오기
async function getSpotifyAccessToken(): Promise<string> {
  // 클라이언트 사이드에서는 Spotify API를 직접 호출할 수 없음
  if (typeof window !== "undefined") {
    throw new Error("Spotify API는 서버 사이드에서만 사용 가능합니다");
  }

  const clientId = process.env.SPOTIFY_CLIENT_ID;
  const clientSecret = process.env.SPOTIFY_CLIENT_SECRET;

  if (!clientId || !clientSecret) {
    throw new Error(
      "Spotify API credentials not found in environment variables"
    );
  }

  const response = await fetch("https://accounts.spotify.com/api/token", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: `Basic ${Buffer.from(`${clientId}:${clientSecret}`).toString("base64")}`,
    },
    body: "grant_type=client_credentials",
  });

  if (!response.ok) {
    throw new Error("Failed to get Spotify access token");
  }

  const data = await response.json();
  return data.access_token;
}

// Key 번호를 음계로 변환
function keyToNote(key: number, mode: number): string {
  const notes = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
  ];
  const modeStr = mode === 1 ? "Major" : "Minor";
  return `${notes[key]} ${modeStr}`;
}

// Valence(긍정도)를 감정으로 매핑
function valenceToEmotion(valence: number, energy: number): string {
  if (valence > 0.7 && energy > 0.7) return "happy";
  if (valence > 0.7 && energy < 0.3) return "calm";
  if (valence < 0.3 && energy > 0.7) return "aggressive";
  if (valence < 0.3 && energy < 0.3) return "sad";
  return "energetic";
}

// Audio Features에서 무드 추론
function audioFeaturesToMood(features: SpotifyAudioFeatures): string {
  const { energy, valence, acousticness, danceability } = features;

  if (acousticness > 0.7 && energy < 0.4) return "peaceful";
  if (danceability > 0.7 && energy > 0.6) return "energetic";
  if (valence > 0.6 && energy > 0.5) return "warm";
  if (valence < 0.4 && energy < 0.5) return "melancholic";
  if (energy > 0.8) return "intense";

  return "warm";
}

/**
 * Spotify Track ID로 오디오 특성 분석
 *
 * 참고: 사용자가 업로드한 파일을 직접 분석할 수 없음
 * AcoustID를 사용해 트랙을 식별한 후 Spotify ID를 얻어야 함
 */
export interface SpotifyRecommendationItem {
  trackId: string;
  title: string;
  artist: string;
  album: string;
  albumArt: string;
  previewUrl?: string | null;
  externalUrl: string;
}

export async function analyzeSpotifyTrack(trackId: string) {
  try {
    const accessToken = await getSpotifyAccessToken();

    // Audio Features 가져오기
    const featuresResponse = await fetch(
      `https://api.spotify.com/v1/audio-features/${trackId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!featuresResponse.ok) {
      throw new Error("Failed to get audio features from Spotify");
    }

    const features: SpotifyAudioFeatures = await featuresResponse.json();

    // Audio Analysis 가져오기 (더 상세한 정보)
    const analysisResponse = await fetch(
      `https://api.spotify.com/v1/audio-analysis/${trackId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    let analysis: SpotifyAudioAnalysis | null = null;
    if (analysisResponse.ok) {
      analysis = await analysisResponse.json();
    }

    // Key 정보 (Audio Analysis 우선, 없으면 Audio Features 사용)
    const finalKey = analysis?.track.key ?? features.key;
    const finalMode = analysis?.track.mode ?? features.mode;
    const keyConfidence = analysis?.track.key_confidence ?? 0.85;

    console.log("🎹 Spotify Key 정보:", {
      key: finalKey,
      mode: finalMode,
      keyString: keyToNote(finalKey, finalMode),
      confidence: keyConfidence,
    });

    // 분석 결과 통합
    return {
      tempo: Math.round(features.tempo),
      key: keyToNote(finalKey, finalMode),
      timeSignature: `${features.time_signature}/4`,
      duration: Math.round(features.duration_ms / 1000),

      energyLevel: features.energy,
      rhythmStability: analysis?.track.tempo_confidence || 0.85,
      dynamicRange: 1 - features.instrumentalness, // 대략적 추정

      emotion: valenceToEmotion(features.valence, features.energy),
      mood: audioFeaturesToMood(features),

      // Spotify 특성을 감정 점수로 변환
      emotionScores: {
        happy: features.valence * features.energy,
        sad: (1 - features.valence) * (1 - features.energy),
        energetic: features.energy * features.danceability,
        calm: features.acousticness * (1 - features.energy),
        aggressive: features.energy * (1 - features.valence) * 0.5,
      },

      confidence:
        ((analysis?.track.tempo_confidence || 0.85) +
          keyConfidence +
          (analysis?.track.mode_confidence || 0.85)) /
        3,
      audioFeatures: features,
    };
  } catch (error) {
    console.error("Spotify analysis error:", error);
    throw error;
  }
}

export async function getTrackRecommendations(
  trackId: string,
  audioFeatures?: Partial<SpotifyAudioFeatures>
): Promise<SpotifyRecommendationItem[]> {
  try {
    const accessToken = await getSpotifyAccessToken();

    const params = new URLSearchParams({
      seed_tracks: trackId,
      limit: "6",
    });

    if (audioFeatures?.tempo) {
      params.set("target_tempo", audioFeatures.tempo.toString());
    }
    if (audioFeatures?.energy) {
      params.set("target_energy", audioFeatures.energy.toString());
    }
    if (audioFeatures?.danceability) {
      params.set("target_danceability", audioFeatures.danceability.toString());
    }
    if (audioFeatures?.valence) {
      params.set("target_valence", audioFeatures.valence.toString());
    }

    const response = await fetch(
      `https://api.spotify.com/v1/recommendations?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      console.warn("Failed to fetch Spotify recommendations", response.status);
      return [];
    }

    const data = await response.json();

    if (!data.tracks) {
      return [];
    }

    const uniqueArtists = new Map<string, SpotifyRecommendationItem>();

    for (const track of data.tracks) {
      if (!track?.artists?.[0]) continue;
      const artistName = track.artists[0].name as string;

      if (uniqueArtists.has(artistName)) {
        continue;
      }

      uniqueArtists.set(artistName, {
        trackId: track.id,
        title: track.name,
        artist: artistName,
        album: track.album?.name || "",
        albumArt: track.album?.images?.[0]?.url || "",
        previewUrl: track.preview_url,
        externalUrl: track.external_urls?.spotify || "",
      });

      if (uniqueArtists.size >= 3) {
        break;
      }
    }

    return Array.from(uniqueArtists.values());
  } catch (error) {
    console.error("Spotify recommendations error:", error);
    return [];
  }
}

/**
 * Spotify에서 트랙 검색 (개선된 알고리즘)
 * 여러 검색 전략을 시도하여 최상의 결과 반환
 */
export async function searchSpotifyTrack(query: {
  artist?: string;
  track?: string;
  album?: string;
}): Promise<string | null> {
  try {
    const accessToken = await getSpotifyAccessToken();

    // 검색 전략 1: artist + track (가장 정확)
    if (query.artist && query.track) {
      const searchQuery1 = `artist:"${query.artist}" track:"${query.track}"`;
      const result1 = await trySearch(accessToken, searchQuery1);
      if (result1) return result1;
    }

    // 검색 전략 2: track만 (아티스트 정보 없을 때)
    if (query.track) {
      const searchQuery2 = `track:"${query.track}"`;
      const result2 = await trySearch(accessToken, searchQuery2);
      if (result2) return result2;
    }

    // 검색 전략 3: 따옴표 없이 재시도 (오타 허용)
    if (query.artist && query.track) {
      const searchQuery3 = `${query.artist} ${query.track}`;
      const result3 = await trySearch(accessToken, searchQuery3);
      if (result3) return result3;
    }

    // 검색 전략 4: 제목만 (마지막 수단)
    if (query.track) {
      const searchQuery4 = query.track;
      const result4 = await trySearch(accessToken, searchQuery4);
      if (result4) return result4;
    }

    console.log("❌ 모든 검색 전략 실패");
    return null;
  } catch (error) {
    console.error("Spotify search error:", error);
    return null;
  }
}

/**
 * 단일 검색 시도
 */
async function trySearch(
  accessToken: string,
  searchQuery: string
): Promise<string | null> {
  try {
    console.log("🔍 Spotify 검색 시도:", searchQuery);

    const response = await fetch(
      `https://api.spotify.com/v1/search?q=${encodeURIComponent(searchQuery)}&type=track&limit=5`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as SpotifySearchResponse;

    if (data.tracks?.items && data.tracks.items.length > 0) {
      // 인기도가 가장 높은 트랙 선택
      const bestTrack = data.tracks.items.reduce((best, current) =>
        current.popularity > best.popularity ? current : best
      );

      console.log(
        "✅ Spotify 트랙 발견:",
        bestTrack.name,
        "-",
        bestTrack.artists[0].name
      );
      return bestTrack.id;
    }

    return null;
  } catch (error) {
    console.error("❌ Spotify 검색 오류:", error);
    return null;
  }
}

/**
 * Spotify 트랙 메타데이터 가져오기
 */
export async function getSpotifyTrackMetadata(
  trackId: string
): Promise<SpotifyTrackMetadata | null> {
  try {
    const accessToken = await getSpotifyAccessToken();

    // 트랙 정보 가져오기
    const trackResponse = await fetch(
      `https://api.spotify.com/v1/tracks/${trackId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!trackResponse.ok) {
      return null;
    }

    const track = (await trackResponse.json()) as SpotifyTrackApiResponse;

    // 아티스트 정보에서 장르 가져오기
    const artistId = track.artists[0]?.id;
    let genres: string[] = [];
    let artistPopularity: number | undefined;
    let artistFollowers: number | undefined;

    if (artistId) {
      const artistResponse = await fetch(
        `https://api.spotify.com/v1/artists/${artistId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (artistResponse.ok) {
        const artist =
          (await artistResponse.json()) as SpotifyArtistApiResponse;
        genres = artist.genres || [];
        artistPopularity = artist.popularity;
        artistFollowers = artist.followers?.total;
      }
    }

    return {
      title: track.name,
      artist: track.artists.map(a => a.name).join(", "),
      album: track.album.name,
      albumArt: track.album.images?.[0]?.url || "",
      genres,
      releaseDate: track.album.release_date,
      popularity: track.popularity,
      previewUrl: track.preview_url,
      explicit: Boolean(track.explicit),
      durationMs: track.duration_ms,
      spotifyArtistId: artistId,
      artistPopularity,
      artistFollowers,
    };
  } catch (error) {
    console.error("Spotify metadata error:", error);
    return null;
  }
}
